import {
  CHECKERS_SIZE,
  type CheckersMove,
  type CheckersPiece,
  type CheckersState,
  type Coord,
} from '@awwyeah/shared';

/**
 * Checkers display geometry (owner ask 2026-11-03: "your pieces always move up, toward the
 * opponent"). Pure, display-only — the engine and the move log stay in engine coordinates
 * (`row` 0 = seat 0's back rank) and nothing here is ever written to a doc.
 *
 * The engine puts seat 0 on rows 0-2 moving toward row 7, so a straight top-to-bottom draw
 * has seat 0 moving *down*. Seat 0 (and spectators, who get seat 0's view) therefore sees the
 * board turned 180° — row AND column reversed, the way it looks from the other side of a real
 * table. A rotation rather than a mirror keeps the square colours where they belong: every
 * player still has a light square in their bottom-right corner, as on a real board. Seat 1
 * already starts at the bottom, so its view is the identity.
 *
 * Every board interaction (cell layout, tap targets, highlights, last-move marks, animation
 * offsets) goes through `toDisplay`/`toEngine`, so the two can never drift apart.
 */

const LAST = CHECKERS_SIZE - 1;

/** Whether `viewerSeat`'s view is the engine board turned 180°. `null` = spectator, who
 * sees seat 0's view. */
export function isFlippedFor(viewerSeat: 0 | 1 | null): boolean {
  return viewerSeat !== 1;
}

/** Engine coordinate → where it's drawn (`[displayRow, displayCol]`, row 0 at the top). */
export function toDisplay(c: Coord, flipped: boolean): Coord {
  return flipped ? [LAST - c[0], LAST - c[1]] : [c[0], c[1]];
}

/** Drawn position → engine coordinate. A 180° turn is its own inverse, so this is
 * `toDisplay` again — named separately so call sites say which way they're going. */
export function toEngine(d: Coord, flipped: boolean): Coord {
  return toDisplay(d, flipped);
}

/** The engine squares a piece jumped over along `path` (one per two-square hop — English
 * draughts kings move one square, so every capture is a midpoint). */
export function jumpedSquares(from: Coord, path: readonly Coord[]): Coord[] {
  const out: Coord[] = [];
  let prev = from;
  for (const step of path) {
    if (Math.abs(step[0] - prev[0]) === 2) {
      out.push([(step[0] + prev[0]) / 2, (step[1] + prev[1]) / 2]);
    }
    prev = step;
  }
  return out;
}

export interface CheckersHop {
  /** Display-cell offset of the hop's start, relative to the final landing square. */
  dRow: number;
  dCol: number;
  /** True when this hop jumps a piece (the disc lifts higher for it). */
  capture: boolean;
}

/**
 * Keyframe waypoints for sliding a piece from `from` along `path`, in *display* cells
 * relative to the final landing square — the moved disc is drawn at its destination and
 * animated in from `hops[0]` through each later waypoint to `[0, 0]`. Going through
 * `toDisplay` means the slide always heads the way the board is drawn for this viewer.
 */
export function hopWaypoints(from: Coord, path: readonly Coord[], flipped: boolean): CheckersHop[] {
  const end = path[path.length - 1];
  if (!end) return [];
  const endD = toDisplay(end, flipped);
  const hops: CheckersHop[] = [];
  let prev = from;
  for (const step of path) {
    const d = toDisplay(prev, flipped);
    hops.push({
      dRow: d[0] - endD[0],
      dCol: d[1] - endD[1],
      capture: Math.abs(step[0] - prev[0]) === 2,
    });
    prev = step;
  }
  return hops;
}

/** What one accepted piece move did, for last-move marks, capture ghosts, and sound. */
export interface CheckersMoveSummary {
  from: Coord;
  path: Coord[];
  /** Engine squares whose pieces this move took, with what was standing there. */
  captured: { at: Coord; piece: CheckersPiece }[];
  /** The man reached the far rank and was crowned by this move. */
  crowned: boolean;
}

/** Summarises the move that turned `before` into `after`, or `null` when there's nothing to
 * show: the starting frame, a draw offer/accept, or an entry `deriveCheckers` rejected (it
 * changed nothing, so marking its squares would point at a move that never happened). */
export function summarizeMove(
  move: CheckersMove | null | undefined,
  before: CheckersState | undefined,
  after: CheckersState | undefined,
): CheckersMoveSummary | null {
  if (!move || !before || !after) return null;
  if (move.kind !== undefined && move.kind !== 'move') return null;
  if (after.appliedMoves === before.appliedMoves) return null;
  const end = move.path[move.path.length - 1];
  if (!end) return null;
  const captured: CheckersMoveSummary['captured'] = [];
  for (const at of jumpedSquares(move.from, move.path)) {
    const piece = before.board[at[0]]?.[at[1]];
    if (piece) captured.push({ at, piece });
  }
  const startPiece = before.board[move.from[0]]?.[move.from[1]];
  const endPiece = after.board[end[0]]?.[end[1]];
  return {
    from: move.from,
    path: move.path,
    captured,
    crowned: Boolean(startPiece && !startPiece.king && endPiece?.king),
  };
}

/** Pieces each seat still has on `state.board`. */
export function piecesLeft(state: CheckersState): [number, number] {
  const counts: [number, number] = [0, 0];
  for (const row of state.board) for (const cell of row) if (cell) counts[cell.player] += 1;
  return counts;
}
